import { useEffect, useState } from 'react';
import { GradeFormat } from '@gpa-scales/shared-types';
import { convertIBToUSPoints, convertECTSToUSPoints, convertPercentageToUSPoints } from '@gpa-scales/shared-logic/engine/internationalConversion';
import { Select } from 'calc-controls';

interface InternationalGradeInputProps {
  gradeFormat: GradeFormat;
  grade: string;
  onGradeChange: (grade: string) => void;
  onFormatChange: (format: GradeFormat) => void;
}

export function InternationalGradeInput({
  gradeFormat,
  grade,
  onGradeChange,
  onFormatChange,
}: InternationalGradeInputProps) {
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!grade.trim()) {
      setPreview(null);
      return;
    }

    let points: number | null = null;

    switch (gradeFormat) {
      case GradeFormat.IB_1_7: {
        const ib = parseInt(grade);
        points = convertIBToUSPoints(ib);
        break;
      }
      case GradeFormat.ECTS: {
        points = convertECTSToUSPoints(grade);
        break;
      }
      case GradeFormat.Percentage: {
        const pct = parseFloat(grade);
        points = convertPercentageToUSPoints(pct);
        break;
      }
      default:
        setPreview(null);
        return;
    }

    setPreview(points !== null ? `${points.toFixed(1)} US` : null);
  }, [grade, gradeFormat]);

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <div className="flex-1">
          <label htmlFor="grade-format" className="sr-only">Grade Format</label>
          <Select
            inline
            label="Grade Format"
            value={gradeFormat}
            onChange={(v) => onFormatChange(v as GradeFormat)}
            options={[
              { value: GradeFormat.Letter, label: 'Letter (A–F)' },
              { value: GradeFormat.Percentage, label: 'Percentage (0–100)' },
              { value: GradeFormat.Numeric, label: 'Numeric (0–4.0)' },
              { value: GradeFormat.IB_1_7, label: 'IB (1–7)' },
              { value: GradeFormat.ECTS, label: 'ECTS (A–F)' },
            ]}
          />
        </div>
        <div className="flex-1">
          {gradeFormat === GradeFormat.IB_1_7 ? (
            <>
              <label className="sr-only" htmlFor="ib-grade">IB Grade</label>
              <Select
                inline
                label="IB Grade"
                value={grade}
                onChange={onGradeChange}
                options={[
                  { value: '', label: 'Select IB Grade' },
                  ...[7, 6, 5, 4, 3, 2, 1].map((g) => ({ value: String(g), label: String(g) })),
                ]}
              />
            </>
          ) : gradeFormat === GradeFormat.ECTS ? (
            <>
              <label className="sr-only" htmlFor="ects-grade">ECTS Grade</label>
              <Select
                inline
                label="ECTS Grade"
                value={grade}
                onChange={onGradeChange}
                options={[
                  { value: '', label: 'Select ECTS Grade' },
                  ...['A', 'B', 'C', 'D', 'E', 'F'].map((g) => ({ value: g, label: g })),
                ]}
              />
            </>
          ) : (
            <input
              id="grade-input"
              type={gradeFormat === GradeFormat.Percentage || gradeFormat === GradeFormat.Numeric ? 'number' : 'text'}
              value={grade}
              onChange={(e) => onGradeChange(e.target.value)}
              className="w-full rounded-md border border-border px-3 py-2 text-sm"
              aria-label="Grade"
              placeholder={
                gradeFormat === GradeFormat.Percentage
                  ? '0–100'
                  : gradeFormat === GradeFormat.Numeric
                    ? '0.0–4.0'
                    : 'A, B+, C-'
              }
              min={gradeFormat === GradeFormat.Percentage ? '0' : gradeFormat === GradeFormat.Numeric ? '0' : undefined}
              max={gradeFormat === GradeFormat.Percentage ? '100' : gradeFormat === GradeFormat.Numeric ? '4.0' : undefined}
              step={gradeFormat === GradeFormat.Numeric ? '0.1' : undefined}
              required
            />
          )}
        </div>
      </div>
      {preview && (
        <p className="text-xs text-[var(--color-text-muted)]">
          ≈ {preview} points
        </p>
      )}
    </div>
  );
}
